import React from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  CircularProgress,
  Alert,
  Stack,
  Divider
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
  TimelineOppositeContent
} from '@mui/lab';
import {
  CheckCircle as CheckCircleIcon,
  PlayCircle as PlayCircleIcon,
  PauseCircle as PauseCircleIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon
} from '@mui/icons-material';
import { TaskStatusDurationReportDTO } from '../../api/generated/requests/types.gen';
import { useReportsServiceGetApiReportsTasksByTaskIdStatusDuration } from '../../api/generated/queries/queries';
import { getStatusColor, getStatusLabel } from '../../models/TaskDTO';

interface TaskStatusTimelineProps {
  taskId: number;
}

const getStatusIcon = (status: string, isActive: boolean) => {
  const iconProps = { fontSize: 'small' as const };
  
  switch (status) {
    case 'TODO':
      return <ScheduleIcon {...iconProps} />;
    case 'IN_PROGRESS':
      return <PlayCircleIcon {...iconProps} />;
    case 'DONE':
      return <CheckCircleIcon {...iconProps} />;
    case 'CANCELED':
      return <CancelIcon {...iconProps} />;
    default:
      return <PauseCircleIcon {...iconProps} />;
  }
};

const formatDays = (days: number): string => {
  if (days === 0) return '0 dni';
  if (days === 1) return '1 dzień';
  if (days < 5) return `${days} dni`;
  return `${days} dni`;
};

const TaskStatusTimeline: React.FC<TaskStatusTimelineProps> = ({ taskId }) => {
  const { data: report, isLoading, error } = useReportsServiceGetApiReportsTasksByTaskIdStatusDuration({ taskId });

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
        <CircularProgress size={40} />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Ładowanie raportu czasowego...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        Błąd podczas ładowania raportu czasowego zadania
      </Alert>
    );
  }

  if (!report || !report.daysInStatus) {
    return (
      <Alert severity="info" sx={{ m: 2 }}>
        Brak danych o czasie spędzonym w statusach
      </Alert>
    );
  }

  // Convert the daysInStatus object to an array and sort by status order
  const statusOrder = ['TODO', 'IN_PROGRESS', 'DONE', 'CANCELED'];
  const statusEntries = Object.entries(report.daysInStatus)
    .filter(([_, days]) => days > 0)
    .sort(([a], [b]) => {
      const indexA = statusOrder.indexOf(a);
      const indexB = statusOrder.indexOf(b);
      return (indexA === -1 ? 999 : indexA) - (indexB === -1 ? 999 : indexB);
    });

  return (
    <Box sx={{ 
      p: 2, 
      display: 'flex', 
      flexDirection: 'column', 
      height: '100%',
      position: 'relative'
    }}>
      {/* Header with task info */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, backgroundColor: '#f8f9fa' }}>
        <Stack direction="row" spacing={2} alignItems="center">
          <Chip
            label={`Aktualny status: ${getStatusLabel(report.currentStatus || 'TODO')}`}
            size="small"
            sx={{
              backgroundColor: getStatusColor(report.currentStatus || 'TODO'),
              color: 'white',
              fontWeight: 'bold',
              fontSize: '0.7rem' // Smaller font for status info
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Łączny czas: <strong>{formatDays(report.totalDays || 0)}</strong>
          </Typography>
          {report.isActive && (
            <Chip
              label="Aktywne"
              size="small"
              color="success"
              variant="outlined"
              sx={{ fontSize: '0.7rem' }} // Smaller font for status info
            />
          )}
        </Stack>
      </Paper>

      {/* Timeline - flexible container */}
      <Box sx={{ flex: 1, overflow: 'auto' }}>
        <Timeline position="right">
          {statusEntries.map(([status, days], index) => {
            const isCurrentStatus = status === report.currentStatus;
            const isLastItem = index === statusEntries.length - 1;
            
            return (
              <TimelineItem key={status}>
                <TimelineOppositeContent
                  sx={{ 
                    m: 'auto 0',
                    minWidth: '120px',
                    textAlign: 'right',
                    pr: 2
                  }}
                  align="right"
                  variant="body2"
                  color="text.secondary"
                >
                  <Typography variant="body2" fontWeight="bold" sx={{ fontSize: '0.8rem' }}>
                    {formatDays(days)}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.7rem' }}>
                    {((days / (report.totalDays || 1)) * 100).toFixed(1)}% czasu
                  </Typography>
                </TimelineOppositeContent>
                
                <TimelineSeparator>
                  <TimelineDot
                    sx={{
                      backgroundColor: getStatusColor(status as any),
                      color: 'white',
                      border: isCurrentStatus ? '3px solid #1976d2' : 'none',
                      boxShadow: isCurrentStatus ? '0 0 0 2px rgba(25, 118, 210, 0.2)' : 'none'
                    }}
                  >
                    {getStatusIcon(status, isCurrentStatus)}
                  </TimelineDot>
                  {!isLastItem && <TimelineConnector />}
                </TimelineSeparator>
                
                <TimelineContent sx={{ py: '12px', px: 2 }}>
                  <Typography variant="h6" component="span" sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}>
                    {getStatusLabel(status as any)}
                  </Typography>
                  {isCurrentStatus && (
                    <Chip
                      label="Aktualny"
                      size="small"
                      color="primary"
                      sx={{ ml: 1, fontSize: '0.65rem' }} // Smaller font for status info
                    />
                  )}
                  <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5, fontSize: '0.75rem' }}>
                    {status === 'TODO' && 'Zadanie oczekuje na rozpoczęcie'}
                    {status === 'IN_PROGRESS' && 'Zadanie jest w trakcie realizacji'}
                    {status === 'DONE' && 'Zadanie zostało ukończone'}
                    {status === 'CANCELED' && 'Zadanie zostało anulowane'}
                  </Typography>
                </TimelineContent>
              </TimelineItem>
            );
          })}
        </Timeline>
      </Box>

      {/* Summary - positioned at bottom */}
      <Box sx={{ 
        position: 'sticky', 
        bottom: 0, 
        mt: 'auto',
        pt: 2
      }}>
        <Divider sx={{ mb: 2 }} />
        <Paper elevation={1} sx={{ p: 2, backgroundColor: '#f0f7ff' }}>
          <Typography variant="caption" color="text.secondary" sx={{ fontSize: '0.75rem' }}>
            Zadanie przeszło przez <strong>{statusEntries.length}</strong> {statusEntries.length === 1 ? 'status' : statusEntries.length < 5 ? 'statusy' : 'statusów'} 
            {' '}w ciągu <strong>{formatDays(report.totalDays || 0)}</strong>.
            {report.isActive ? ' Zadanie jest obecnie aktywne.' : ' Zadanie nie jest już aktywne.'}
          </Typography>
        </Paper>
      </Box>
    </Box>
  );
};

export default TaskStatusTimeline;